// Pure logic for the Entity assets surface, kept out of the component so the
// address contract and the id rules can be unit tested without rendering
// anything. Same split as lib/promo-codes.ts and lib/advertisers.ts.
//
// The addresses here are a CONVENTION, not a lookup. Every football entity
// asset lives at a deterministic CDN address built from its canonical id, so
// the page renders each layer optimistically and lets the browser's own
// 200/404 answer whether it exists (ADR-036). There is no coverage API to call
// for a single entity and there is deliberately not going to be one.
//
// # TWO VOCABULARIES, AND THEY ARE NOT THE SAME ONE
//
// This file kept ONE list of five words and used it for both the entity's kind
// and the artwork's address. That was the bug. A player's id prefix and a
// player's artwork directory are different facts, and writing them as one value
// produced media/btl/player/ and media/btl/manager/ preview URLs, both of which
// are RETIRED directories that nothing reads and nothing writes.
//
//   EntityKind       what the entity IS. Drives the id prefix, whether search
//                    can find it, and which roles it has.
//   EntityAssetRole  which IMAGE of it is being addressed. Drives the
//                    media/btl/<dir> segment, the accepted formats, and the
//                    upload and delete routes.
//
// They are not one-to-one in either direction. A player has TWO roles, an
// avatar and a hero, at two different keys, which is the whole point of
// ADR-036's role model: replacing one leaves the other alone. A hero belongs to
// two kinds, player and manager, because the identity registry models a manager
// as a Coach and both are the same kind of picture of the same kind of subject.

import { isBtlCdnSafe, matchesSearchQuery } from "@breakingthelines/design-system";

import { mediaCdnBaseUrl } from "@/config/env";

/** What the entity IS. The page's selector, and what identity reports back. */
export type EntityKind = "crest" | "competition" | "player" | "manager" | "stadium";

/**
 * Which image of an entity an address points at.
 *
 * These five strings are also the canonical URL segments the admin routes take,
 * so a role goes into a request path verbatim. The v1 segments (competition,
 * player, manager, stadium) still work server-side as aliases but are not sent
 * from here any more.
 */
export type EntityAssetRole = "crest" | "badge" | "avatar" | "hero" | "image";

/**
 * Which layer of the stack an address belongs to.
 *
 * Four words, and they are not decoration: `provider`, `avatar` and `hero` are
 * three DIFFERENT prefixes under the CDN (provider/, apifootball/, wikimedia/)
 * and which of them a kind has is the thing this page is easiest to be wrong
 * about. `identity` is the odd one out - it is not an address this page builds
 * but a value identity-server reports, which is why it can only ever be
 * rendered after the resolver's safety check.
 */
export type EntityImageLayerKind = "bespoke" | "provider" | "avatar" | "hero" | "identity";

/** One renderable address, with the layer it belongs to. */
export interface EntityImageLayer {
  /** Stable key for React and for tests. */
  id: string;
  /** What this layer is, in the operator's words. */
  label: string;
  /** Absolute CDN URL. */
  url: string;
  /** Which layer of the stack this address is. */
  kind: EntityImageLayerKind;
  /** True for BTL's own art, which is the only layer this page can write. */
  bespoke: boolean;
  /** Attribution that must be shown with the image. Only the identity layer has one. */
  credit?: string;
}

interface ProviderLayerSpec {
  id: string;
  label: string;
  /** Which of the three non-BTL prefixes this address sits under. */
  kind: Exclude<EntityImageLayerKind, "bespoke" | "identity">;
  /** Path under the CDN base, with <id> substituted. */
  path: (entityId: string) => string;
}

const SVG = "image/svg+xml";
const WEBP = "image/webp";
const PNG = "image/png";
const JPEG = "image/jpeg";

/**
 * The raster formats every role takes, in the order the accept list shows them.
 *
 * WebP is what an asset is STORED as. PNG and JPEG are only ever accepted:
 * content-service decodes them, sizes them to the mandate's geometry and
 * re-encodes them as WebP, so the file that lands is a .webp whichever of the
 * three went up.
 *
 * The preview needed no change for this. It probes the CDN at the addresses in
 * `extensions`, and a PNG upload lands at the .webp one, which it was already
 * probing.
 */
const RASTER_TYPES = [WEBP, PNG, JPEG] as const;

export interface EntityAssetRoleSpec {
  role: EntityAssetRole;
  /** Tile heading and the word used in every message about this role. */
  label: string;
  /** What this image is for, shown once under the tile. */
  blurb: string;
  /**
   * The media/btl/<dir> segment. FROM THIS TABLE AND NOWHERE ELSE.
   *
   * Three roles are named after their directory and two are not, and deriving
   * the directory from the role name instead would write media/btl/badge/ and
   * media/btl/image/, which nothing reads. The read side addresses one
   * self-describing segment: "competition" and "stadium" already say what is in
   * the directory, while a directory called "image" would say nothing.
   */
  dir: string;
  /**
   * Extensions the art can be STORED as, in the order the page probes them.
   *
   * Not the same list as `contentTypes` and it did not grow when that one did.
   * A PNG upload is stored as a .webp, so there is no third address to probe.
   */
  extensions: readonly string[];
  /** MIME types this role's upload control accepts. */
  contentTypes: readonly string[];
  /** What the imagery mandate asks for, shown next to the upload control. */
  formatHint: string;
}

/**
 * The five roles, and the whole address table this page writes to.
 *
 *   role     entities            id prefix(es)                dir                    formats
 *   -------- ------------------- ---------------------------- ---------------------- ----------
 *   crest    teams               btl_football_team_           media/btl/crest/       svg, webp
 *   badge    competitions        btl_football_competition_    media/btl/competition/ svg, webp
 *   avatar   players             btl_football_player_         media/btl/avatar/      webp
 *   hero     players, managers   btl_football_player_,        media/btl/hero/        webp
 *                                btl_football_coach_
 *   image    stadiums            btl_football_venue_          media/btl/stadium/     webp
 *
 * Marks are vector first and photographic roles are raster only, so an SVG hero
 * is a rejection rather than a stored file nothing renders.
 */
export const ENTITY_ASSET_ROLES: Record<EntityAssetRole, EntityAssetRoleSpec> = {
  crest: {
    role: "crest",
    label: "Crest",
    blurb: "Club and national team mark.",
    dir: "crest",
    extensions: ["svg", "webp"],
    contentTypes: [SVG, ...RASTER_TYPES],
    formatHint:
      "SVG, PNG, JPEG or WebP. Everything but SVG becomes WebP on a 512x512 square, padded rather than stretched, never enlarged.",
  },
  badge: {
    role: "badge",
    label: "Badge",
    blurb: "League and cup mark.",
    dir: "competition",
    extensions: ["svg", "webp"],
    contentTypes: [SVG, ...RASTER_TYPES],
    formatHint:
      "SVG, PNG, JPEG or WebP. Everything but SVG becomes WebP on a 512x512 square, padded rather than stretched, never enlarged.",
  },
  avatar: {
    role: "avatar",
    label: "Avatar",
    blurb: "Small round headshot.",
    dir: "avatar",
    extensions: ["webp"],
    contentTypes: [...RASTER_TYPES],
    formatHint:
      "PNG, JPEG or WebP. Stored as WebP with the long edge at most 1024. The shape is kept and nothing is enlarged.",
  },
  hero: {
    role: "hero",
    label: "Hero",
    blurb: "Large editorial portrait.",
    dir: "hero",
    extensions: ["webp"],
    contentTypes: [...RASTER_TYPES],
    formatHint:
      "PNG, JPEG or WebP. Stored as WebP with the long edge at most 1024. The shape is kept and nothing is enlarged.",
  },
  image: {
    role: "image",
    label: "Stadium",
    blurb: "Ground photography.",
    dir: "stadium",
    extensions: ["webp"],
    contentTypes: [...RASTER_TYPES],
    formatHint:
      "PNG, JPEG or WebP. Stored as WebP with the long edge at most 1024. The shape is kept and nothing is enlarged.",
  },
};

export function entityAssetRoleSpec(role: EntityAssetRole): EntityAssetRoleSpec {
  const spec = ENTITY_ASSET_ROLES[role];
  if (!spec) throw new Error(`unknown entity asset role: ${role}`);
  return spec;
}

export interface EntityKindSpec {
  value: EntityKind;
  /** Sidebar and select label. */
  label: string;
  /** Canonical identity id prefix. Two of these differ from the kind name. */
  idPrefix: string;
  /**
   * The bespoke roles this kind has, in the order the page shows them.
   *
   * A player has two. Under the entity-type model it had one slot, so an avatar
   * and a hero shared a key and uploading either overwrote the other.
   */
  roles: readonly EntityAssetRole[];
  /**
   * Provider layers platform actually renders for this kind today, taken from
   * the design-system resolver's pinned address contract. NOT a guess: the
   * layer name differs per kind (provider / apifootball / wikimedia) and a
   * stadium has none at all.
   */
  providerLayers: readonly ProviderLayerSpec[];
  /**
   * Can identity-server's search find this kind at all?
   *
   * False for exactly one of the five, and it is a fact about the registry
   * rather than a decision: the deployed SQLite artifact has no venues table,
   * so `Search` never scans one and `Lookup` answers NotFound for every venue
   * id. A stadium is reached by its canonical id or not at all.
   */
  searchable: boolean;
}

/**
 * The five kinds, and the whole vocabulary of this page's selector.
 *
 * Two things here are easy to get wrong and are written out rather than
 * derived. First, a manager is a Coach and a stadium is a Venue in the identity
 * registry, so the id prefix does not match the kind name for either. Second,
 * the provider layer is not one uniform prefix: crests and competition marks
 * sit under provider/, player headshots under apifootball/, player and manager
 * portraits under wikimedia/, and venues have no provider layer whatsoever.
 * Those addresses come from the design-system resolver, which is what every
 * public surface builds from.
 */
export const ENTITY_KINDS: readonly EntityKindSpec[] = [
  {
    value: "crest",
    label: "Club",
    idPrefix: "btl_football_team_",
    roles: ["crest"],
    providerLayers: [
      {
        id: "provider-crest",
        label: "Provider crest",
        kind: "provider",
        path: (id) => `provider/crest/${id}.png`,
      },
    ],
    searchable: true,
  },
  {
    value: "competition",
    label: "Competition",
    idPrefix: "btl_football_competition_",
    roles: ["badge"],
    providerLayers: [
      {
        id: "provider-competition",
        label: "Provider badge",
        kind: "provider",
        path: (id) => `provider/competition/${id}.png`,
      },
    ],
    searchable: true,
  },
  {
    value: "player",
    label: "Player",
    idPrefix: "btl_football_player_",
    // Both, and in this order. The avatar is the one with no v1 spelling: under
    // the entity-type model the only player asset an admin could write was the
    // portrait, so an avatar has never been uploadable until now.
    roles: ["avatar", "hero"],
    providerLayers: [
      {
        id: "provider-avatar",
        label: "Provider headshot",
        kind: "avatar",
        path: (id) => `apifootball/player/${id}.png`,
      },
      {
        id: "provider-hero",
        label: "Provider portrait",
        kind: "hero",
        path: (id) => `wikimedia/player/${id}.jpg`,
      },
    ],
    searchable: true,
  },
  {
    value: "manager",
    label: "Manager",
    idPrefix: "btl_football_coach_",
    // One. A manager has an editorial portrait and no avatar: the avatar role
    // pins btl_football_player_ ids only, so a coach avatar would be refused
    // server-side and would never resolve if it were not.
    roles: ["hero"],
    providerLayers: [
      {
        id: "provider-hero",
        label: "Provider portrait",
        kind: "hero",
        path: (id) => `wikimedia/manager/${id}.jpg`,
      },
    ],
    searchable: true,
  },
  {
    value: "stadium",
    label: "Stadium",
    idPrefix: "btl_football_venue_",
    roles: ["image"],
    // None. There is no venue layer in the resolver's address contract, so a
    // stadium has bespoke art or a monogram and nothing in between.
    providerLayers: [],
    searchable: false,
  },
];

const KINDS_BY_VALUE = new Map(ENTITY_KINDS.map((spec) => [spec.value, spec]));

export function entityKindSpec(kind: EntityKind): EntityKindSpec {
  const spec = KINDS_BY_VALUE.get(kind);
  if (!spec) throw new Error(`unknown entity kind: ${kind}`);
  return spec;
}

/**
 * Which kind an id belongs to, read off its prefix.
 *
 * The id is the only thing that says what an entity is, so this is a derivation
 * and not a guess. It is what lets a row in the bespoke art listing open the
 * right editor: an object key gives a role, and a hero could be either a player
 * or a manager, but the id says which.
 *
 * Null for an id outside the five kinds this page edits.
 */
export function entityKindForId(entityId: string): EntityKind | null {
  const trimmed = entityId.trim();
  const match = ENTITY_KINDS.find((spec) => trimmed.startsWith(spec.idPrefix));
  return match ? match.value : null;
}

/**
 * Does this id look like a canonical identity id for this kind?
 *
 * Mirrors the rule content-service enforces: the kind's prefix, then ASCII
 * letters and digits only. Player ids are not purely numeric in practice
 * (btl_football_player_p32813799), so digits alone would reject real entities.
 *
 * This is a form check, not the authority. The server validates independently
 * and refuses anything else before a key exists; checking here only saves the
 * operator a round trip and tells them which rule they broke.
 */
export function isCanonicalEntityId(kind: EntityKind, entityId: string): boolean {
  const spec = entityKindSpec(kind);
  const trimmed = entityId.trim();
  if (trimmed.length === 0 || trimmed.length > 128) return false;
  if (!trimmed.startsWith(spec.idPrefix)) return false;
  const suffix = trimmed.slice(spec.idPrefix.length);
  return /^[A-Za-z0-9]+$/.test(suffix);
}

/**
 * Object key for BTL's own art, matching what content-service derives
 * server-side. Built here only to render a preview and to show the operator
 * where the file went; the upload itself never sends a key.
 *
 * KEYED BY ROLE, and the directory comes from the role table. This used to
 * interpolate the entity kind, which put a player's preview at
 * media/btl/player/ and a manager's at media/btl/manager/ - two directories
 * that were retired with the role model and that nothing reads or writes. The
 * upload succeeded and the preview beside it pointed at nothing.
 */
export function bespokeObjectKey(
  role: EntityAssetRole,
  entityId: string,
  extension: string,
): string {
  return `media/btl/${entityAssetRoleSpec(role).dir}/${entityId}.${extension}`;
}

/**
 * The CDN addresses BTL's own art for one role could be at, one per extension.
 *
 * More than one for the mark roles because a crest may be stored as SVG or as
 * WebP and nothing here knows which without asking the CDN. The tiles probe
 * both and the 404 answers.
 */
export function bespokeRoleLayers(
  role: EntityAssetRole,
  entityId: string,
  cdnBase: string = mediaCdnBaseUrl(),
): EntityImageLayer[] {
  const spec = entityAssetRoleSpec(role);
  const base = cdnBase.replace(/\/+$/, "");
  return spec.extensions.map((extension) => ({
    id: `btl-${role}-${extension}`,
    // The format, and nothing else. The tile sits under the role's own heading
    // inside the artwork section, so "which file is this" is the only question
    // the caption still has to answer.
    label: extension === "svg" ? "SVG" : "WebP",
    // The bespoke key starts "media/" and the base already ends in "/media",
    // so the shared segment is dropped rather than doubled.
    url: `${base}/btl/${spec.dir}/${entityId}.${extension}`,
    kind: "bespoke",
    bespoke: true,
  }));
}

export interface BespokeRoleTile {
  role: EntityAssetRole;
  spec: EntityAssetRoleSpec;
  /** One probe address per extension this role accepts. */
  layers: EntityImageLayer[];
}

/**
 * Every bespoke role this kind has, each with its probe addresses.
 *
 * The tile unit is the ROLE and not the extension, because the role is what an
 * upload and a delete are scoped to: DeleteEntityAsset removes every canonical
 * extension for one role and leaves the entity's other roles alone. A player
 * gets two of these and they are independently editable.
 */
export function bespokeRoleTiles(
  kind: EntityKind,
  entityId: string,
  cdnBase: string = mediaCdnBaseUrl(),
): BespokeRoleTile[] {
  return entityKindSpec(kind).roles.map((role) => ({
    role,
    spec: entityAssetRoleSpec(role),
    layers: bespokeRoleLayers(role, entityId, cdnBase),
  }));
}

/**
 * The mirrored provider addresses for this kind, which this page cannot write.
 *
 * They are a WRONG-ID DETECTOR. A real entity in the launch corpus almost
 * always has a mirrored provider image, so a provider tile that 404s next to a
 * plausible looking id usually means the id is wrong. Art filed under a wrong
 * id uploads cleanly and then never resolves.
 */
export function providerImageLayers(
  kind: EntityKind,
  entityId: string,
  cdnBase: string = mediaCdnBaseUrl(),
): EntityImageLayer[] {
  const base = cdnBase.replace(/\/+$/, "");
  return entityKindSpec(kind).providerLayers.map((provider) => ({
    id: provider.id,
    label: provider.label,
    url: `${base}/${provider.path(entityId)}`,
    kind: provider.kind,
    bespoke: false,
  }));
}

/**
 * Resolve a backend-supplied media value to something safe to put in a `src`,
 * or null.
 *
 * Null for anything that is not on the BTL CDN. identity's `image_url` is a
 * value from a data pipeline, and the pipeline's upstream is Wikimedia; a raw
 * provider hotlink reaching an `<img>` here would leak an admin's IP to a third
 * party, hand that party a referrer naming an internal tool, and render an
 * image nobody at BTL controls. `isBtlCdnSafe` is the design-system's own
 * predicate, imported rather than restated so this page and every reader
 * surface answer identically.
 *
 * The `/media` collapse mirrors the resolver's `joinCdn`: the base already ends
 * in `/media` and the key also starts `media/`, so the shared segment is
 * dropped rather than doubled.
 */
export function safeMediaUrl(
  value: string | null | undefined,
  cdnBase: string = mediaCdnBaseUrl(),
): string | null {
  const trimmed = value?.trim();
  if (!trimmed || !isBtlCdnSafe(trimmed)) return null;
  if (/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(trimmed)) return trimmed;

  const base = cdnBase.replace(/\/+$/, "");
  const lastSegment = base.slice(base.lastIndexOf("/") + 1);
  let path = trimmed.replace(/^\/+/, "");
  if (lastSegment && (path === lastSegment || path.startsWith(`${lastSegment}/`))) {
    path = path.slice(lastSegment.length).replace(/^\/+/, "");
  }
  return path ? `${base}/${path}` : base;
}

/**
 * identity's own image for this entity, as a layer tile, or null.
 *
 * The only tile whose address this page did not construct: identity reports it,
 * and it is shown so an operator can see the image the registry itself holds
 * before deciding whether BTL needs its own. Null when identity has no image,
 * and null when the one it has is not on the BTL CDN, which is not an error
 * state to report - it is a value this surface simply will not render.
 */
export function identityImageLayer(
  imageUrl: string | null | undefined,
  imageCredit: string | null | undefined,
  cdnBase: string = mediaCdnBaseUrl(),
): EntityImageLayer | null {
  const url = safeMediaUrl(imageUrl, cdnBase);
  if (!url) return null;
  return {
    id: "identity-image",
    label: "Reference image",
    url,
    kind: "identity",
    bespoke: false,
    credit: imageCredit?.trim() || undefined,
  };
}

/**
 * Cache-buster for a preview the operator has just written.
 *
 * Stored entity assets carry a 5-minute max-age on an unversioned key, so a
 * browser that cached the old art can keep showing it for a few minutes after
 * a replacement lands. The public URL stays clean; only this page's own
 * preview gets the parameter, so the operator sees their own write
 * immediately instead of waiting out the window.
 */
export function previewUrl(url: string, writtenAt: number | null): string {
  if (writtenAt === null) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${writtenAt}`;
}

/**
 * Is this an SVG upload?
 *
 * This used to be `uploadModeFor`, which answered "sanitise" or "presign" and
 * picked one of two routes. There is one route now: EVERY upload goes through
 * content-service. The two formats still get different treatment there, and
 * neither treatment is something a presigned PUT could do, because a presigned
 * PUT goes straight to object storage and never passes through the service at
 * all.
 *
 *   SVG      sanitised. Raw SVG served from our own CDN origin is a
 *            stored-XSS vector, and nothing could strip it after the fact.
 *   raster   decoded, sized to the mandate's geometry, re-encoded as WebP.
 *            A presigned PNG would land verbatim under a .webp key that no
 *            reader could decode.
 *
 * So the answer is no longer a route. It is only which of the two things the
 * server will do, which is what the size cap turns on.
 */
export function isSvgUpload(contentType: string): boolean {
  return contentType.trim().toLowerCase().startsWith(SVG);
}

/**
 * `accept` for a role's file input.
 *
 * MIME types AND extensions. The MIME types are the authority - they are what
 * `isAcceptedFormat` checks and what the server is told - but a file picker on
 * some systems reports nothing useful for a .jpg, and both spellings of the
 * JPEG extension are in daily use. Listing both makes the picker filter
 * correctly without changing what is actually accepted.
 */
export function acceptAttribute(role: EntityAssetRole): string {
  const extensions: Record<string, readonly string[]> = {
    [SVG]: [".svg"],
    [WEBP]: [".webp"],
    [PNG]: [".png"],
    [JPEG]: [".jpg", ".jpeg"],
  };
  const types = entityAssetRoleSpec(role).contentTypes;
  return [...types, ...types.flatMap((type) => extensions[type] ?? [])].join(",");
}

/**
 * Is this file one of the formats the ROLE accepts?
 *
 * Per role, not per kind, and that distinction is now load bearing: a player
 * takes WebP for both roles, but a crest takes SVG while nothing photographic
 * does. Checking against the kind would have let an SVG through to a hero.
 *
 * The browser's `accept` is a filter, not a gate, and a file picked through
 * "All files" arrives with any type at all. The server refuses it either way;
 * this turns that into an immediate message instead of a failed upload.
 */
export function isAcceptedFormat(role: EntityAssetRole, contentType: string): boolean {
  const normalized = contentType.trim().toLowerCase().split(";")[0].trim();
  return entityAssetRoleSpec(role).contentTypes.includes(normalized);
}

/** Formats this role takes, for the message shown when one is rejected. */
export function acceptedFormatsLabel(role: EntityAssetRole): string {
  // Spelled the way the format is spelled: "WEBP" is a MIME fragment shouted
  // back at the operator, and this string goes into a sentence.
  const names: Record<string, string> = {
    [SVG]: "SVG",
    [WEBP]: "WebP",
    [PNG]: "PNG",
    [JPEG]: "JPEG",
  };
  const labels = entityAssetRoleSpec(role).contentTypes.map((value) => names[value] ?? value);
  // "A, B or C" rather than "A or B or C" once there are more than two.
  if (labels.length <= 2) return labels.join(" or ");
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

/**
 * Cache-Control the stored asset carries.
 *
 * This string is SIGNED INTO a presigned PUT, so a browser using that route
 * must send it back verbatim or the signature does not verify and R2 refuses
 * the upload. It must stay identical to `entityasset.CacheControl` in
 * content-service; the two are a matched pair and there is no negotiation
 * between them.
 *
 * THIS PAGE NO LONGER PRESIGNS. Every upload goes through content-service so
 * that SVG is sanitised and raster is sized to the mandate's geometry, and the
 * service sets this header itself on the object it writes. The constant stays
 * because the pair still has to match: the presign RPC is still there, still
 * signs this value, and a future caller that uses it would break on a
 * mismatch exactly as before.
 *
 * Decided in content-service#215: bespoke art must be updatable on the fly, so
 * this is a 5-minute max-age on the stable, unversioned key rather than the
 * year-long immutable value it used to be. A REPLACED asset can still be
 * stale for the length of the window, which is why the preview on this page
 * also cache-busts its own URL after a write, but readers now see a
 * replacement within minutes instead of up to a year.
 */
export const ENTITY_ASSET_CACHE_CONTROL = "public, max-age=300";

/** 512 KiB for SVG, 5 MiB for raster, matching the server's caps exactly. */
export const MAX_SVG_BYTES = 512 * 1024;
export const MAX_RASTER_BYTES = 5 * 1024 * 1024;

export function maxBytesFor(contentType: string): number {
  return isSvgUpload(contentType) ? MAX_SVG_BYTES : MAX_RASTER_BYTES;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Shared date rendering for every table and detail panel on this surface: the
 * text corrections, the artwork rows beside them, and the Entity tab's "last
 * edited" line. One place so the two tabs cannot drift onto different formats.
 */
export function formatDateTime(value?: string): string {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/** The role filter on the bespoke art listing, in address-table order. */
export const ENTITY_ASSET_ROLE_FILTERS: readonly EntityAssetRole[] = [
  "crest",
  "badge",
  "avatar",
  "hero",
  "image",
];

// # ONE LIST, ONE FACET
//
// Corrections shows two sources that were two tabs: the text overrides, which
// game-service pages by DB cursor most recent first, and the bespoke art, which
// content-service pages by bucket prefix in lexicographic order. Those cursors
// are not comparable, so nothing here merges them into one chronological
// stream; what unifies them is the vocabulary below and the facet built on it.
//
// The facet replaced two dropdowns, a kind filter on the text list and a role
// filter on the art list, with the one question an operator actually asks:
// WHAT CHANGED. The kind narrowing is not lost with the dropdown - an entity id
// is built from identity's own kind word, so typing "coach" into the search box
// still finds every correction on a manager.

/**
 * What changed on an entity: a text field or one of the five images.
 *
 * The two text values are fields on an override row. The five image values are
 * `EntityAssetRole` verbatim, so a facet can go into a listing request as a
 * role without translation.
 */
export type EntityChangeFacet = "all" | "name" | "biography" | EntityAssetRole;

/** The "no filter" value, kept out of the list below so it cannot be sent as a role. */
export const ALL_ENTITY_CHANGES = "all";

/**
 * The facet's options in reading order: the text fields first, then the images
 * in address-table order. Every label is the operator's word for the thing,
 * which for the `image` role is "Stadium" and never "image".
 */
export const ENTITY_CHANGE_FACETS: readonly { value: EntityChangeFacet; label: string }[] = [
  { value: "name", label: "Name" },
  { value: "biography", label: "Biography" },
  ...ENTITY_ASSET_ROLE_FILTERS.map((role) => ({
    value: role as EntityChangeFacet,
    label: entityAssetRoleSpec(role).label,
  })),
];

/** The facet's label, for the closed select. */
export function entityChangeFacetLabel(facet: EntityChangeFacet): string {
  return ENTITY_CHANGE_FACETS.find((option) => option.value === facet)?.label ?? "All changes";
}

/**
 * Which of the two sources a facet draws from.
 *
 * This is what makes the facet a router rather than a filter: a text facet has
 * no artwork to show and an image facet has no text to show, so the single list
 * is fed by one source or, on "All changes", by both under their own headings.
 */
export function entityChangeSources(facet: EntityChangeFacet): {
  text: boolean;
  artwork: boolean;
} {
  if (facet === ALL_ENTITY_CHANGES) return { text: true, artwork: true };
  if (facet === "name" || facet === "biography") return { text: true, artwork: false };
  return { text: false, artwork: true };
}

/**
 * The role to narrow the artwork listing to server-side, or undefined for every
 * role.
 *
 * An image facet is narrowed BY THE SERVER, which is why picking one pages
 * through every crest in the store rather than through whichever crests
 * happened to be on the loaded pages. The two text facets have no equivalent:
 * game-service pages overrides by cursor and cannot filter them by which field
 * was written, so those narrow over loaded rows and the page says so.
 */
export function entityChangeRole(facet: EntityChangeFacet): EntityAssetRole | undefined {
  return entityChangeSources(facet).artwork && facet !== ALL_ENTITY_CHANGES
    ? (facet as EntityAssetRole)
    : undefined;
}

/**
 * What a text correction changed, in the operator's words.
 *
 * A row can carry both fields, and both are shown: an override is a FULL-ROW
 * write (see setEntityOverride), so a non-empty field is a field this admin
 * replaced. Empty is not a correction - a row with neither field set is deleted
 * server-side rather than stored - so the empty array is a defensive case, not
 * a state the list is expected to render.
 */
export function overrideChangeLabels(override: {
  displayName: string;
  biography: string;
}): string[] {
  const changed: string[] = [];
  if (override.displayName.trim()) changed.push("Name");
  if (override.biography.trim()) changed.push("Biography");
  return changed;
}

/** Does this correction match the facet? Client-side: see entityChangeRole. */
export function overrideMatchesChangeFacet(
  override: { displayName: string; biography: string },
  facet: EntityChangeFacet,
): boolean {
  if (facet === ALL_ENTITY_CHANGES) return true;
  if (facet === "name") return override.displayName.trim().length > 0;
  if (facet === "biography") return override.biography.trim().length > 0;
  return false;
}

/**
 * Does a row match what the operator typed?
 *
 * The id and the corrected name, and nothing else. Neither source has a search
 * endpoint, so this runs over the rows already loaded and the page reports how
 * many that was; matching against something the row does not show would make
 * the count unreadable.
 *
 * `matchesSearchQuery` is the design-system's own predicate, imported rather
 * than restated so a name with an accent is found by its unaccented spelling
 * here exactly as it is on every reader surface.
 */
export function entityRowMatchesQuery(
  row: { entityId: string; displayName?: string },
  query: string,
): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;
  return (
    matchesSearchQuery(row.entityId, trimmed) || matchesSearchQuery(row.displayName ?? "", trimmed)
  );
}
